<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, transferStatusLabel } from '../utils/format'
import type { FinancialTransfer } from '../types/finance'

const { t } = useI18n()
const auth = useAuthStore()
const { listFinancialTransfers, reverseFinancialTransfer } = useFinanceApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('treasury_transactions.manage'))

const rows = ref<FinancialTransfer[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref<string>('')

const filters = ref({
  status: '' as '' | 'posted' | 'reversed',
  search: '',
})

const visibleRows = computed(() => {
  const term = filters.value.search.trim().toLowerCase()
  return rows.value.filter((row) => {
    if (filters.value.status && row.status !== filters.value.status) return false
    if (!term) return true
    return [row.reference, row.notes, row.source_account_name, row.destination_account_name]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term))
  })
})

const postedCount = computed(() => rows.value.filter((row) => row.status === 'posted').length)

async function load() {
  loading.value = true
  error.value = ''
  try {
    rows.value = await listFinancialTransfers()
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function reverse(transfer: FinancialTransfer) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('finance.transfers.reverseConfirm'))) return
  busyId.value = String(transfer.id)
  try {
    await reverseFinancialTransfer(transfer.id)
    actionMessage.value = t('finance.transfers.reversed')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = ''
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.transfers.title') }}</h2>
  </div>

  <div class="toolbar">
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('finance.common.searchPlaceholder')"
    >
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option value="posted">{{ t('finance.transferStatus.posted') }}</option>
      <option value="reversed">{{ t('finance.transferStatus.reversed') }}</option>
    </select>
    <span class="spacer" />
    <div class="summary">
      <span><strong>{{ postedCount }}</strong> {{ t('finance.transferStatus.posted') }}</span>
      <span>{{ t('finance.common.rows', { count: visibleRows.length }) }}</span>
    </div>
    <RouterLink v-if="canManage" class="btn-primary" to="/treasury/transfers/new">
      {{ t('finance.transfers.newTransfer') }}
    </RouterLink>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <div v-else-if="visibleRows.length === 0" class="state-message">
    <p>{{ t('finance.transfers.empty') }}</p>
  </div>

  <div v-else class="table-scroll">
    <table class="data-table">
      <thead>
        <tr>
          <th>{{ t('finance.transfers.transferDate') }}</th>
          <th>{{ t('finance.transfers.sourceAccount') }}</th>
          <th>{{ t('finance.transfers.destinationAccount') }}</th>
          <th class="cell-num">{{ t('finance.transfers.sourceAmount') }}</th>
          <th class="cell-num">{{ t('finance.transfers.destinationAmount') }}</th>
          <th class="cell-num">{{ t('finance.common.exchangeRate') }}</th>
          <th class="cell-num">{{ t('finance.transfers.feeAmount') }}</th>
          <th>{{ t('finance.common.reference') }}</th>
          <th>{{ t('finance.common.status') }}</th>
          <th class="cell-actions">{{ t('finance.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="transfer in visibleRows" :key="transfer.id">
          <td class="cell-date">{{ formatDisplayDate(transfer.transfer_date) }}</td>
          <td class="cell-name">{{ transfer.source_account_name || transfer.source_account_id }}</td>
          <td class="cell-name">
            {{ transfer.destination_account_name || transfer.destination_account_id }}
          </td>
          <td class="cell-num">
            {{ formatMoney(transfer.source_amount, transfer.source_currency_code) }}
          </td>
          <td class="cell-num">
            {{ formatMoney(transfer.destination_amount, transfer.destination_currency_code) }}
          </td>
          <td class="cell-num">{{ transfer.exchange_rate }}</td>
          <td class="cell-num">
            {{ transfer.fee_amount ? formatMoney(transfer.fee_amount, transfer.source_currency_code) : '—' }}
          </td>
          <td>{{ transfer.reference || '—' }}</td>
          <td>
            <span
              class="status-badge"
              :class="transfer.status === 'posted' ? 'status-ok' : 'status-critical'"
            >
              {{ transferStatusLabel(t, transfer.status) }}
            </span>
          </td>
          <td class="cell-actions">
            <button
              v-if="canManage && transfer.status === 'posted'"
              class="link-action danger"
              :disabled="busyId === String(transfer.id)"
              @click="reverse(transfer)"
            >
              {{ t('finance.transfers.reverse') }}
            </button>
            <span v-else class="empty-inline">—</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
